import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountCardRow, AccountGridRow, AccountRow } from './AccountRows';
import { useListFetch } from './useListFetch';
import { ListStateShell } from './ListStateShell';

/** AccountsGrid — #62 Phase 2 (UXeng's per-screen spec §3). Same responsive tier-switch as
 *  LeadsGrid/ContactsGrid; fetches `/api/companies` (CompanyService.getAccounts). */
export const AccountsGrid: React.FC<{
  selectedId?: string | null;
  onOpenAccount: (account: AccountRow) => void;
  /** Bumped by the parent after create/delete succeeds (#62 Accounts depth spec) — this component owns
   *  its own fetch, so it needs an explicit re-fetch signal. */
  refreshKey?: number;
  /** D-C1 — the empty state carries the create affordance, not just copy. */
  onCreate?: () => void;
}> = ({ selectedId, onOpenAccount, refreshKey, onCreate }) => {
  const { t } = useTranslation('common');
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' && window.innerWidth >= 1024);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // X-1 via useListFetch (P38 surface 1 of 10): loading is a skeleton, a failed load is an ERROR
  // with retry, and the empty copy below can only ever render on a trustworthy empty list.
  const list = useListFetch<AccountRow[]>('/api/companies', {
    refreshKey,
    map: (rows) => (rows as any[]).map((c) => ({
      id: c.id, name: c.name || '—', industry: c.industry ?? null, location: c.location ?? null,
      primaryContactName: c.primaryContactName ?? null, totalDealValue: c.totalDealValue ?? 0,
    })),
  });
  const accounts = list.data ?? [];

  return (
    <div className="relative w-full">
      <ListStateShell status={list.status} testIdBase="accounts-grid" onRetry={list.retry}>
      {!isDesktop ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
          {accounts.map((a) => <AccountCardRow key={a.id} account={a} selected={a.id === selectedId} onOpenAccount={onOpenAccount} />)}
          {accounts.length === 0 && (
            <div className="col-span-full text-center text-sm text-text-muted py-10 flex flex-col items-center gap-3">
              {t('accountsGrid.empty')}
              {onCreate && <button type="button" data-testid="accounts-empty-create" onClick={onCreate} className="min-h-[44px] px-4 rounded-lg bg-primary text-white text-sm font-semibold">{t('accountsGrid.emptyCreate')}</button>}
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-start border-collapse">
            <thead>
              <tr className="text-label-caps uppercase text-text-muted border-b border-border-neutral">
                <th className="sticky z-10 bg-surface p-cell-y px-cell-x text-start [inset-inline-start:0]">{t('accountsGrid.columns.name')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('accountsGrid.columns.industry')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('accountsGrid.columns.location')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('accountsGrid.columns.primaryContact')}</th>
                <th className="p-cell-y px-cell-x text-start">{t('accountsGrid.columns.totalDealValue')}</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => <AccountGridRow key={a.id} account={a} selected={a.id === selectedId} onOpenAccount={onOpenAccount} />)}
            </tbody>
          </table>
          {accounts.length === 0 && (
            <div className="text-center text-sm text-text-muted py-10 flex flex-col items-center gap-3">
              {t('accountsGrid.empty')}
              {onCreate && <button type="button" data-testid="accounts-empty-create" onClick={onCreate} className="min-h-[44px] px-4 rounded-lg bg-primary text-white text-sm font-semibold">{t('accountsGrid.emptyCreate')}</button>}
            </div>
          )}
        </div>
      )}
      </ListStateShell>
    </div>
  );
};
